/**
 * Le glossaire d'une affaire — le vocabulaire graphique de l'instantané actif.
 *
 * L'onglet répond à une question précise : « que veulent dire les symboles, les
 * repères, les unités de ces plans ? ». Il lit l'instantané ACTIF (le même que
 * les missions), en inventorie les BLOCS DXF (les symboles, rendus en image
 * vectorielle de référence), les repérages (les étiquettes, par famille), la
 * nomenclature des calques (la légende de facto) et les unités de chaque
 * planche.
 *
 * **La frontière déterministe tient.** Ce qui est compté, situé ou dessiné vient
 * du DXF, sans modèle. Seule la description d'un symbole — le sens — est
 * proposée par une flotte légère, avec une confiance : un avis à relire, jamais
 * une mesure. Aucun nombre de métré n'est produit ici.
 *
 * Comme l'onglet Instantanés n'expose pas l'instantané actif au détail de
 * l'affaire, ce composant va le chercher lui-même (`/instantanes`) et se garde
 * de lancer quoi que ce soit tant qu'aucun n'est actif.
 */
import { useEffect, useMemo, useState } from "react";

import { api, ErreurApi, type Page } from "../../api/client";
import type {
  EntreeGlossaire,
  Instantane,
  Profil,
  RapportGlossaire,
  Travail,
} from "../../api/types";
import { Avis } from "../../composants/avis";
import { Chargement, Echec } from "../../composants/etats";
import {
  TRAVAIL_EN_COURS,
  useDonnees,
  useRelance,
  useSuivreLaFile,
  type Veille,
} from "../../composants/donnees";

/** Les familles de symboles, dans l'ordre d'affichage, avec leur libellé. La
 *  flotte range dans ce vocabulaire ; « autre » ramasse le reste. */
const FAMILLES: Record<string, string> = {
  menuiserie: "Menuiserie",
  mobilier: "Mobilier",
  sanitaire: "Sanitaire",
  electricite: "Électricité",
  plomberie_cvc: "Plomberie / CVC",
  revetement: "Revêtements",
  accessibilite: "Accessibilité",
  reperage: "Repérage",
  annotation: "Annotation",
  structure: "Structure",
  nord_echelle: "Nord / échelle",
  autre: "Autre",
};

type Categorie =
  | "symboles" | "menuiseries" | "reperages" | "legendes" | "nomenclature"
  | "cctp" | "unites";

export function Glossaire({
  affaireId,
  veille,
  profil = null,
  surAffectation,
}: {
  affaireId: string;
  veille: Veille<Travail>;
  profil?: Profil | null;
  surAffectation?: () => void;
}) {
  const [envoi, poserEnvoi] = useState(false);
  const [erreur, poserErreur] = useState("");

  // L'instantané actif, cherché ici même : le glossaire n'a de sens qu'avec lui.
  const jeux = useDonnees<Page<Instantane>>(
    () => api.lire<Page<Instantane>>(`/api/v1/affaires/${affaireId}/instantanes`),
    [affaireId],
  );
  const rapport = useDonnees<RapportGlossaire | null>(
    () => api.lire<RapportGlossaire | null>(`/api/v1/affaires/${affaireId}/glossaire`),
    [affaireId],
  );

  const donnees = rapport.donnees ?? null;
  const reportEnCours = donnees != null && TRAVAIL_EN_COURS.has(donnees.etat);
  // Un travail de glossaire vu de la veille — il comble le court instant entre le
  // clic et l'ouverture du rapport : le rapport montre encore l'ancien inventaire
  // (READY) alors que le nouveau travail est déjà « en file d'attente ».
  const jobGloss = veille.travaux.find(
    (t) => t.genre === "GLOSSAIRE" && TRAVAIL_EN_COURS.has(t.etat),
  );
  const enCours = reportEnCours || jobGloss != null;

  // La veille réveille l'écran aux changements d'état ; pendant le balayage, on
  // relit à cadence rapprochée pour voir la barre et les planches défiler.
  useSuivreLaFile(veille.empreinte, rapport.recharger);
  useRelance(rapport.recharger, 2000, enCours);

  const actif = (jeux.donnees?.donnees ?? []).find((j) => j.etat === "ACTIVE");

  async function lancer() {
    poserEnvoi(true);
    poserErreur("");
    try {
      await api.ecrire(`/api/v1/affaires/${affaireId}/glossaire`);
      veille.rafraichir();
      rapport.recharger();
    } catch (exc) {
      poserErreur(
        exc instanceof ErreurApi
          ? exc.message
          : "L'inventaire n'a pas pu être lancé. Réessayez.",
      );
    } finally {
      poserEnvoi(false);
    }
  }

  if (jeux.charge && !jeux.donnees) {
    return <div className="contenu"><Chargement lignes={3} /></div>;
  }
  if (jeux.erreur) {
    return (
      <div className="contenu">
        <Echec erreur={jeux.erreur} reessayer={jeux.recharger} />
      </div>
    );
  }

  // Sans instantané actif, rien à inventorier : on le dit, sans bouton.
  if (!actif) {
    return (
      <div className="contenu pile">
        <Avis ton="attention" permanent>
          Aucun instantané actif. Le glossaire lit l'instantané actif du dossier :
          préparez puis activez un instantané dans l'onglet <b>Instantanés</b>, et
          revenez inventorier son vocabulaire.
        </Avis>
      </div>
    );
  }

  // On distingue explicitement les états terminaux : abouti (READY) montre le
  // résultat ; échec et annulé le disent chacun à leur façon. Le double négatif
  // « !enCours && !echec » faisait passer un travail ANNULÉ pour un inventaire
  // vide et abouti.
  const echec = donnees?.etat === "FAILED";
  const abouti = donnees?.etat === "READY";
  const annule = donnees != null && !enCours && !echec && !abouti;
  // L'inventaire affiché peut porter sur une génération antérieure (relevé sur la
  // gén. 2, puis la gén. 3 activée) : on le dit plutôt que d'étiqueter les
  // données d'une génération qui n'est pas la leur.
  const genRapport = donnees?.generation ?? null;
  const perime =
    abouti && genRapport != null && actif.generation != null
      && genRapport !== actif.generation;

  return (
    <div className="contenu pile">
      {erreur && <Avis ton="erreur" cle={erreur}>{erreur}</Avis>}

      <div className="carte">
        <div className="carte__corps pile">
          <div className="rangee rangee--espacee">
            <div className="pile pile--serree" style={{ minWidth: 0 }}>
              <h2>Glossaire de l'instantané</h2>
              <span className="discret">
                Symboles, repérages, légendes, nomenclature et unités relevés sur
                les plans préparés de l'instantané
                {(perime ? genRapport : actif.generation)
                  ? ` (génération ${perime ? genRapport : actif.generation})`
                  : ""}.
              </span>
            </div>
            <div className="rangee" style={{ gap: "var(--mo-space-2)" }}>
              <AffecterProfil
                affaireId={affaireId} profil={profil}
                surAffectation={surAffectation}
              />
              <button
                type="button"
                className="bouton bouton--primaire"
                disabled={envoi || enCours}
                onClick={lancer}
              >
                {enCours && <span className="spinner spinner--bouton" aria-hidden="true" />}
                {enCours
                  ? "Inventaire en cours…"
                  : donnees
                    ? "Relancer l'inventaire"
                    : "Inventorier le glossaire"}
              </button>
            </div>
          </div>

          {enCours && (
            <ProgresInventaire
              progression={reportEnCours ? (donnees?.progression ?? 0)
                : (jobGloss?.progression ?? 0)}
              restantS={reportEnCours ? (donnees?.restant_s ?? null) : null}
              feuilles={reportEnCours ? (donnees?.feuilles ?? 0) : 0}
              symboles={reportEnCours ? (donnees?.compte.symboles ?? 0) : 0}
              enFile={!reportEnCours}
            />
          )}
          {echec && (
            <Avis ton="erreur" permanent>
              L'inventaire a échoué{donnees?.detail ? ` : ${donnees.detail}` : "."}
            </Avis>
          )}
          {annule && (
            <Avis ton="attention" permanent>
              Le dernier inventaire a été annulé avant de s'établir. Relancez-le
              pour dresser le glossaire.
            </Avis>
          )}
          {perime && (
            <Avis ton="attention" permanent>
              Cet inventaire porte sur la génération {genRapport} ; l'instantané
              actif est la génération {actif.generation}. Relancez l'inventaire
              pour le mettre à jour.
            </Avis>
          )}

          {abouti && <ResumeVue rapport={donnees!} />}
        </div>
      </div>

      {abouti && <GlossaireCategories rapport={donnees!} />}

      {!donnees && !enCours && (
        <Avis ton="info" permanent>
          Aucun inventaire n'a encore tourné sur cet instantané. « Inventorier le
          glossaire » lit toutes les planches, recense les symboles (avec leur
          image), les repérages, les légendes et les unités, puis fait décrire
          chaque symbole. Le relevé peut prendre quelques minutes.
        </Avis>
      )}
    </div>
  );
}

/** La barre d'avancement de l'inventaire en cours : une phase parlante, la
 *  jauge, les comptes qui défilent, et un temps restant estimé qui s'égrène.
 *
 *  L'avancement (0–100) est celui que le travail écrit au fil de l'eau — la même
 *  valeur que le tiroir Traitements. Le temps restant vient du serveur, qui mêle
 *  l'historique de l'affaire et l'avancement ; il s'égrène ici seconde par
 *  seconde entre deux relectures, et se recale à chaque réponse. */
function ProgresInventaire({
  progression, restantS, feuilles, symboles, enFile,
}: {
  progression: number;
  restantS: number | null;
  feuilles: number;
  symboles: number;
  enFile: boolean;
}) {
  const restant = useCompteARebours(restantS, !enFile);
  const pct = Math.max(0, Math.min(100, Math.round(progression)));
  return (
    <div className="progres" role="status" aria-live="polite">
      <div className="rangee rangee--espacee">
        <span className="progres__phase">
          <span className="spinner" aria-hidden="true" />
          {phaseInventaire(pct, enFile)}
        </span>
        <span className="progres__pct nombre">{enFile ? "" : `${pct} %`}</span>
      </div>
      <div
        className="jauge jauge--haute"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={enFile ? undefined : pct}
      >
        <div
          className={`jauge__part${enFile ? " jauge__part--indefini" : ""}`}
          style={enFile ? undefined : { width: `${pct}%` }}
        />
      </div>
      <div className="rangee rangee--espacee progres__pied">
        <span className="discret">
          {feuilles > 0 && `${feuilles} planche${feuilles > 1 ? "s" : ""} lue${feuilles > 1 ? "s" : ""}`}
          {feuilles > 0 && symboles > 0 && " · "}
          {symboles > 0 && `${symboles} symbole${symboles > 1 ? "s" : ""} trouvé${symboles > 1 ? "s" : ""}`}
          {feuilles === 0 && symboles === 0 && "Préparation du balayage…"}
        </span>
        <span className="discret nombre">
          {enFile
            ? "En file d'attente"
            : restant != null
              ? `Encore ~${formatDuree(restant)}`
              : "Estimation du temps…"}
        </span>
      </div>
    </div>
  );
}

/** La phase lisible tirée du pourcentage — elle épouse les paliers que le travail
 *  écrit (lecture des DXF 5→45 %, sources PDF/CCTP 45→60 %, description 60→90 %). */
function phaseInventaire(pct: number, enFile: boolean): string {
  if (enFile) return "En file d'attente…";
  if (pct < 5) return "Démarrage de l'inventaire…";
  if (pct < 45) return "Lecture des planches…";
  if (pct < 60) return "Lecture des nomenclatures PDF et du CCTP…";
  if (pct < 90) return "Description des symboles…";
  return "Finalisation de l'inventaire…";
}

/** Un temps en français, sobre : « 45 s », « 2 min 30 s », « 1 h 05 min ». */
function formatDuree(secondes: number): string {
  const s = Math.max(0, Math.round(secondes));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m < 60) return r ? `${m} min ${r} s` : `${m} min`;
  const h = Math.floor(m / 60);
  return `${h} h ${String(m % 60).padStart(2, "0")} min`;
}

/** Un compte à rebours lisse : il part de la valeur du serveur, s'égrène chaque
 *  seconde, et se recale dès qu'une nouvelle valeur arrive (toutes les ~2 s). Un
 *  temps qui saute par paliers de deux secondes se lirait comme une horloge
 *  cassée ; celui-ci descend régulièrement. */
function useCompteARebours(valeur: number | null, actif: boolean): number | null {
  const [affiche, poser] = useState<number | null>(valeur);

  // Recalage : chaque réponse du serveur redevient la vérité.
  useEffect(() => {
    poser(valeur);
  }, [valeur]);

  useEffect(() => {
    if (!actif || valeur == null) return;
    const minuteur = window.setInterval(() => {
      poser((s) => (s == null ? null : Math.max(0, s - 1)));
    }, 1000);
    return () => window.clearInterval(minuteur);
  }, [actif, valeur]);

  return affiche;
}

/** La navigation par catégorie + les sections — réutilisée telle quelle par la
 *  page d'un profil, sur un glossaire consolidé de même forme. */
export function GlossaireCategories({ rapport }: { rapport: RapportGlossaire }) {
  const [categorie, poserCategorie] = useState<Categorie>("symboles");
  return (
    <>
      <NavCategories rapport={rapport} actif={categorie} choisir={poserCategorie} />
      {categorie === "symboles" && <SectionSymboles entrees={rapport.symboles} />}
      {categorie === "menuiseries" && <SectionMenuiseries entrees={rapport.menuiseries} />}
      {categorie === "reperages" && <SectionReperages entrees={rapport.reperages} />}
      {categorie === "legendes" && <SectionLegendes entrees={rapport.legendes} />}
      {categorie === "nomenclature" && <SectionNomenclature entrees={rapport.calques} />}
      {categorie === "cctp" && <SectionCCTP entrees={rapport.lots ?? []} />}
      {categorie === "unites" && <SectionUnites entrees={rapport.unites} />}
    </>
  );
}

/** « Affecter à un profil » — un menu déroulant des profils de l'espace ; l'affaire
 *  s'y rattache (ou s'en détache), et le profil consolidera son glossaire. */
function AffecterProfil({
  affaireId, profil, surAffectation,
}: {
  affaireId: string;
  profil: Profil | null;
  surAffectation?: () => void;
}) {
  const [ouvert, poserOuvert] = useState(false);
  const [envoi, poserEnvoi] = useState(false);
  const liste = useDonnees<{ donnees: Profil[] }>(
    () => api.lire<{ donnees: Profil[] }>("/api/v1/profils"),
    [],
  );
  const profils = liste.donnees?.donnees ?? [];

  async function affecter(profilId: string | null) {
    poserEnvoi(true);
    try {
      await api.modifier(`/api/v1/affaires/${affaireId}/profil`,
                         { profil_id: profilId });
      surAffectation?.();
    } catch {
      /* le refus reste silencieux ici : l'écran garde l'ancien rattachement */
    } finally {
      poserEnvoi(false);
      poserOuvert(false);
    }
  }

  return (
    <div className="glossaire-profil">
      <button
        type="button"
        className="bouton bouton--discret"
        disabled={envoi}
        onClick={() => poserOuvert((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={ouvert}
      >
        {profil ? `Profil : ${profil.nom}` : "Affecter à un profil"}
      </button>
      {ouvert && (
        <div className="glossaire-profil__menu" role="listbox">
          {profil && (
            <button type="button" className="glossaire-profil__opt" role="option"
                    aria-selected={false} onClick={() => affecter(null)}>
              Détacher
            </button>
          )}
          {profils.length === 0 && (
            <div className="glossaire-profil__vide discret">
              Aucun profil. Créez-en un dans l'onglet <b>Profils</b>.
            </div>
          )}
          {profils.map((p) => (
            <button
              key={p.profil_id} type="button" className="glossaire-profil__opt"
              role="option" aria-selected={profil?.profil_id === p.profil_id}
              onClick={() => affecter(p.profil_id)}
            >
              {p.nom}
              {p.cabinet ? <span className="discret"> · {p.cabinet}</span> : null}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Le bandeau de résumé : les comptes, les familles, et l'alerte d'unités. */
export function ResumeVue({ rapport }: { rapport: RapportGlossaire }) {
  const r = rapport.resume ?? {};
  const alertes = r.alertes ?? [];
  return (
    <div className="pile pile--serree">
      <div className="glossaire-comptes">
        <Compte n={rapport.compte.symboles} mot="symboles" />
        <Compte n={rapport.menuiseries.length} mot="menuiseries" />
        <Compte n={rapport.compte.reperages} mot="repérages" />
        <Compte n={rapport.calques.length} mot="calques" />
        <Compte n={rapport.compte.legendes} mot="légendes" />
        {(rapport.lots ?? []).length > 0 && (
          <Compte n={(rapport.lots ?? []).length} mot="lots CCTP" />
        )}
        <Compte n={rapport.feuilles} mot="planches" />
        {(r.unites ?? []).length > 0 && (
          <Compte n={(r.unites ?? []).length} mot="unités" />
        )}
      </div>
      {alertes.map((a) => (
        <Avis key={a} ton="attention" permanent>{a}</Avis>
      ))}
    </div>
  );
}

function Compte({ n, mot }: { n: number; mot: string }) {
  return (
    <div className="glossaire-compte">
      <span className="glossaire-compte__n">{n}</span>
      <span className="glossaire-compte__mot">{mot}</span>
    </div>
  );
}

function NavCategories({
  rapport,
  actif,
  choisir,
}: {
  rapport: RapportGlossaire;
  actif: Categorie;
  choisir: (c: Categorie) => void;
}) {
  const entrees: [Categorie, string, number][] = [
    ["symboles", "Symboles", rapport.symboles.length],
    ["menuiseries", "Menuiseries", rapport.menuiseries.length],
    ["reperages", "Repérages", rapport.reperages.length],
    ["legendes", "Légendes", rapport.legendes.length],
    ["nomenclature", "Nomenclature", rapport.calques.length],
    ["cctp", "CCTP", (rapport.lots ?? []).length],
    ["unites", "Unités", rapport.unites.length],
  ];
  return (
    <div className="segments" role="tablist" aria-label="Catégories du glossaire">
      {entrees.map(([clef, libelle, n]) => (
        <button
          key={clef}
          type="button"
          role="tab"
          aria-selected={actif === clef}
          className={actif === clef ? "segments__actif" : ""}
          onClick={() => choisir(clef)}
        >
          {libelle} <span className="discret">{n}</span>
        </button>
      ))}
    </div>
  );
}

// ── Symboles ────────────────────────────────────────────────────────────────

function SectionSymboles({ entrees }: { entrees: EntreeGlossaire[] }) {
  const [famille, poserFamille] = useState<string>("");
  const [q, poserQ] = useState("");

  const familles = useMemo(() => {
    const compte = new Map<string, number>();
    for (const e of entrees) compte.set(e.famille, (compte.get(e.famille) ?? 0) + 1);
    return [...compte.entries()].sort((a, b) => b[1] - a[1]);
  }, [entrees]);

  const visibles = useMemo(() => {
    const terme = q.trim().toLowerCase();
    return entrees.filter(
      (e) =>
        (!famille || e.famille === famille) &&
        (!terme ||
          e.nom.toLowerCase().includes(terme) ||
          e.description.toLowerCase().includes(terme)),
    );
  }, [entrees, famille, q]);

  if (entrees.length === 0) {
    return <Avis ton="info" permanent>Aucun symbole relevé sur cet instantané.</Avis>;
  }

  return (
    <div className="pile">
      <div className="rangee rangee--espacee glossaire-filtres">
        <div className="glossaire-familles">
          <button
            type="button"
            className={`glossaire-fchip${!famille ? " glossaire-fchip--actif" : ""}`}
            onClick={() => poserFamille("")}
          >
            Toutes <span className="discret">{entrees.length}</span>
          </button>
          {familles.map(([f, n]) => (
            <button
              key={f}
              type="button"
              className={`glossaire-fchip${famille === f ? " glossaire-fchip--actif" : ""}`}
              onClick={() => poserFamille(famille === f ? "" : f)}
            >
              {FAMILLES[f] ?? f} <span className="discret">{n}</span>
            </button>
          ))}
        </div>
        <input
          type="search"
          className="glossaire-recherche"
          placeholder="Chercher un symbole…"
          value={q}
          onChange={(e) => poserQ(e.target.value)}
          aria-label="Chercher un symbole par nom ou description"
        />
      </div>

      <div className="glossaire-grille">
        {visibles.map((e) => (
          <SymboleCarte key={e.nom} entree={e} />
        ))}
      </div>
      {visibles.length === 0 && (
        <Avis ton="info" permanent>Aucun symbole ne correspond à ce filtre.</Avis>
      )}
    </div>
  );
}

/** Citation CCTP : `{lot, article, page, reference, extrait}`. */
interface CitationCctp {
  lot?: string | null; article?: string | null; page?: number | null;
  reference?: string; extrait?: string;
}

/** Les pastilles de PROVENANCE : d'où vient le sens de l'entrée (dessin, PDF,
 *  CCTP). C'est la lecture hybride rendue visible. */
function Provenance({ valeur }: { valeur?: string }) {
  const p = valeur || "dxf";
  if (p === "pdf") {
    return (
      <span className="glossaire-prov">
        <span className="glossaire-prov__b glossaire-prov__b--pdf"
              title="Lu en nomenclature PDF, absent du dessin DXF">PDF seul</span>
      </span>
    );
  }
  const parts: [string, string, string][] = [];
  if (p.includes("dxf")) parts.push(["dxf", "DXF", "Vu au dessin (DXF)"]);
  if (p.includes("pdf")) parts.push(["pdf", "PDF", "Défini en nomenclature PDF"]);
  if (p.includes("cctp")) parts.push(["cctp", "CCTP", "Prescrit au CCTP"]);
  return (
    <span className="glossaire-prov">
      {parts.map(([k, lbl, t]) => (
        <span key={k} className={`glossaire-prov__b glossaire-prov__b--${k}`} title={t}>
          {lbl}
        </span>
      ))}
    </span>
  );
}

/** Le bloc d'évidence normative : caractéristiques lues à la lettre + citations
 *  du CCTP. Chaque citation renvoie à son lot / article / page. */
function CctpBloc({ cctp, caracteristiques }: {
  cctp?: CitationCctp[]; caracteristiques?: Record<string, string[]>;
}) {
  const carac = Object.entries(caracteristiques ?? {});
  const cits = cctp ?? [];
  if (!carac.length && !cits.length) return null;
  return (
    <div className="glossaire-cctp">
      {carac.length > 0 && (
        <div className="glossaire-cctp__carac">
          {carac.map(([k, vs]) => (
            <span key={k} className="glossaire-cctp__c" title={`${k} (CCTP)`}>
              <b>{k}</b> {vs.slice(0, 3).join(", ")}
            </span>
          ))}
        </div>
      )}
      {cits.map((c, i) => (
        <span key={i} className="glossaire-cctp__ref" title={c.extrait || ""}>
          CCTP · {c.reference || [c.lot && `lot ${c.lot}`, c.article && `art. ${c.article}`,
            c.page && `p.${c.page}`].filter(Boolean).join(" · ") || "?"}
        </span>
      ))}
    </div>
  );
}

function SymboleCarte({ entree }: { entree: EntreeGlossaire }) {
  const planches = entree.emplacements.length;
  const d = entree.donnees as {
    appels?: number; texte?: string; provenance?: string;
    cctp?: CitationCctp[]; caracteristiques?: Record<string, string[]>;
    legende_pdf?: { code?: string; definition?: string; page?: number } | null;
  };
  const appels = d.appels ?? entree.occurrences;
  const texteBloc = (d.texte ?? "").trim();
  return (
    <figure className="glossaire-carte">
      <div className="glossaire-vignette">
        {entree.image_svg ? (
          <SvgSymbole svg={entree.image_svg} titre={entree.nom} />
        ) : (
          <span className="glossaire-vignette__vide" aria-hidden="true">⬚</span>
        )}
      </div>
      <figcaption className="glossaire-carte__corps">
        <div className="rangee rangee--espacee">
          <span className="glossaire-carte__fam">
            {FAMILLES[entree.famille] ?? entree.famille}
          </span>
          {entree.confiance && <PastilleConfiance niveau={entree.confiance} />}
        </div>
        <p className="glossaire-carte__desc">
          {entree.description || <span className="discret">Sans description.</span>}
        </p>
        {d.legende_pdf?.definition && (
          <span className="glossaire-carte__pdf" title={`Nomenclature PDF (p.${d.legende_pdf.page ?? "?"})`}>
            📄 {d.legende_pdf.code ? `${d.legende_pdf.code} — ` : ""}
            {d.legende_pdf.definition}
          </span>
        )}
        <CctpBloc cctp={d.cctp} caracteristiques={d.caracteristiques} />
        {texteBloc && (
          <span className="glossaire-carte__texte" title={texteBloc}>« {texteBloc} »</span>
        )}
        <span className="glossaire-carte__nom mono tronque" title={entree.nom}>
          {entree.nom}
        </span>
        <div className="rangee rangee--espacee">
          <div className="glossaire-carte__pied discret">
            {/* Objets physiques dédoublonnés ; les appels bruts (répétés sur les
                planches de détail) en second, pour ne pas gonfler le compte. */}
            <b>{entree.occurrences}</b> objet{entree.occurrences > 1 ? "s" : ""}
            {appels > entree.occurrences ? ` · ${appels} appels` : ""}
            {" · "}
            {planches} planche{planches > 1 ? "s" : ""}
          </div>
          <Provenance valeur={d.provenance} />
        </div>
      </figcaption>
    </figure>
  );
}

/** Rend un symbole en `<img>` par une URI `data:` — et NON en SVG inline.
 *
 *  Chaque SVG d'ezdxf déclare ses couleurs dans un `<style>` par classes
 *  génériques (`.C1`, `.C2`…). Inlinés dans le DOM, les 300 blocs `<style>` de
 *  la grille se marcheraient dessus : le dernier symbole repeindrait tous les
 *  autres. En image `data:`, chaque SVG est une ressource isolée — ses styles
 *  n'agissent qu'en son sein — et la CSP l'autorise (`img-src … data:`). */
function SvgSymbole({ svg, titre }: { svg: string; titre: string }) {
  const uri = useMemo(
    () => "data:image/svg+xml," + encodeURIComponent(nettoyer(svg)),
    [svg],
  );
  return (
    <img className="glossaire-svg" src={uri} alt={`Symbole : ${titre}`} loading="lazy" />
  );
}

function nettoyer(svg: string): string {
  // Pas de <script>, pas d'attribut on…= : ni l'un ni l'autre ne devrait exister
  // dans une sortie ezdxf, mais une image tierce mérite cette ceinture.
  return svg
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/\son\w+\s*=\s*"[^"]*"/gi, "")
    .replace(/\son\w+\s*=\s*'[^']*'/gi, "");
}

function PastilleConfiance({ niveau }: { niveau: string }) {
  const libelle =
    niveau === "haute" ? "Description sûre"
      : niveau === "moyenne" ? "Description plausible"
        : "Description déduite, à relire";
  return (
    <span
      className={`glossaire-conf glossaire-conf--${niveau}`}
      title={libelle}
      aria-label={libelle}
    >
      {niveau === "haute" ? "●" : niveau === "moyenne" ? "◐" : "○"}
    </span>
  );
}

// ── Menuiseries (nomenclatures typées) ──────────────────────────────────────

function SectionMenuiseries({ entrees }: { entrees: EntreeGlossaire[] }) {
  if (entrees.length === 0) {
    return (
      <Avis ton="info" permanent>
        Aucune nomenclature de menuiseries relevée (repères N0 / EV / PE).
      </Avis>
    );
  }
  return (
    <div className="pile">
      {entrees.map((e) => {
        const d = e.donnees as {
          type?: string; codes?: string[]; appels?: number;
          niveaux?: string[]; calques?: string[];
          provenance?: string; pdf_seule?: boolean;
          source_pdf?: string; page?: number;
          cctp?: CitationCctp[]; caracteristiques?: Record<string, string[]>;
          lignes_pdf?: { code?: string; definition?: string; page?: number }[];
        };
        const codes = d.codes ?? [];
        const lignes = d.lignes_pdf ?? [];
        return (
          <div key={e.nom} className="carte">
            <div className="carte__corps pile pile--serree">
              <div className="rangee rangee--espacee">
                <div className="pile pile--serree" style={{ minWidth: 0 }}>
                  <h3 style={{ margin: 0 }}>{e.nom}</h3>
                  <span className="discret mono">
                    {d.pdf_seule ? d.source_pdf : (d.calques ?? []).join(" · ")}
                  </span>
                </div>
                <div className="rangee" style={{ gap: "var(--mo-space-2)", alignItems: "center" }}>
                  <span className="glossaire-genre">{d.type}</span>
                  <Provenance valeur={d.pdf_seule ? "pdf" : d.provenance} />
                </div>
              </div>
              <div className="discret">
                <b>{codes.length}</b> repère{codes.length > 1 ? "s" : ""} distinct
                {codes.length > 1 ? "s" : ""}
                {d.appels ? ` · ${d.appels} appels` : ""}
                {(d.niveaux ?? []).length
                  ? ` · niveaux : ${(d.niveaux ?? []).join(", ")}` : ""}
                {lignes.length ? ` · ${lignes.length} défini(s) en nomenclature PDF` : ""}
              </div>

              <CctpBloc cctp={d.cctp} caracteristiques={d.caracteristiques} />

              {lignes.length > 0 ? (
                <div className="defilant">
                  <table className="tableau glossaire-table glossaire-nomtable">
                    <thead>
                      <tr><th>Repère</th><th>Désignation (nomenclature PDF)</th></tr>
                    </thead>
                    <tbody>
                      {lignes.map((l, i) => (
                        <tr key={`${l.code}-${i}`}>
                          <td className="mono">{l.code}</td>
                          <td>{(l.definition ?? "").replace(/\n+/g, " ")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="glossaire-valeurs">
                  {codes.map((c) => (
                    <span key={c} className="glossaire-valeur mono">{c}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── CCTP (lots normatifs du DCE) ─────────────────────────────────────────────

function SectionCCTP({ entrees }: { entrees: EntreeGlossaire[] }) {
  if (entrees.length === 0) {
    return (
      <Avis ton="info" permanent>
        Aucun lot de CCTP indexé pour cet instantané. Le CCTP est le vocabulaire
        normatif du dossier : une fois la pièce écrite préparée, ses lots et
        articles enrichissent ici chaque symbole et chaque nomenclature.
      </Avis>
    );
  }
  return (
    <div className="carte">
      <div className="carte__corps pile pile--serree">
        <span className="discret">
          Les lots du CCTP indexé — le squelette normatif du DCE. C'est cette pièce
          écrite qui prescrit les caractéristiques citées sur les symboles et les
          menuiseries (coupe-feu, acoustique, matériaux…).
        </span>
        <div className="defilant">
          <table className="tableau glossaire-table">
            <thead>
              <tr><th>Lot</th><th>Intitulé</th><th>Articles</th></tr>
            </thead>
            <tbody>
              {entrees.map((e, i) => {
                const d = e.donnees as { code?: string; titre?: string; articles?: number };
                return (
                  <tr key={`${d.code}-${i}`}>
                    <td className="mono">{d.code}</td>
                    <td>{d.titre}</td>
                    <td className="mono">{d.articles ?? e.occurrences}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Repérages ────────────────────────────────────────────────────────────────

const GENRES: Record<string, string> = {
  REPERE: "Repère",
  LOCAL: "Local",
  CARTOUCHE: "Cartouche",
  ANNOTATION: "Annotation",
};

function SectionReperages({ entrees }: { entrees: EntreeGlossaire[] }) {
  if (entrees.length === 0) {
    return <Avis ton="info" permanent>Aucun repérage relevé sur cet instantané.</Avis>;
  }
  return (
    <div className="pile">
      {entrees.map((e) => {
        const d = e.donnees as {
          calque?: string; genre?: string; valeurs?: string[]; etat?: string;
        };
        const valeurs = d.valeurs ?? [];
        return (
          <div key={e.nom + (d.calque ?? "")} className="carte">
            <div className="carte__corps pile pile--serree">
              <div className="rangee rangee--espacee">
                <div className="pile pile--serree" style={{ minWidth: 0 }}>
                  <h3 style={{ margin: 0 }}>{e.nom}</h3>
                  <span className="discret mono">{d.calque}</span>
                </div>
                <span className="glossaire-genre">{GENRES[d.genre ?? ""] ?? d.genre}</span>
              </div>
              <div className="discret">
                <b>{e.occurrences}</b> valeur{e.occurrences > 1 ? "s" : ""} distincte
                {e.occurrences > 1 ? "s" : ""} · vu sur {e.emplacements.length} planche
                {e.emplacements.length > 1 ? "s" : ""}
              </div>
              <div className="glossaire-valeurs">
                {valeurs.slice(0, 40).map((v) => (
                  <span key={v} className="glossaire-valeur mono">{v.replace(/\n+/g, " ")}</span>
                ))}
                {/* Le « +N » compte sur le TOTAL distinct (occurrences), pas sur
                    l'échantillon stocké — sinon on masquerait la troncature. */}
                {e.occurrences > 40 && (
                  <span className="discret">+{e.occurrences - 40}…</span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Légendes ─────────────────────────────────────────────────────────────────

function SectionLegendes({ entrees }: { entrees: EntreeGlossaire[] }) {
  if (entrees.length === 0) {
    return (
      <Avis ton="info" permanent>
        Aucun bloc de légende dessiné n'a été trouvé sur ces plans. À défaut, la{" "}
        <b>Nomenclature</b> (onglet voisin) recense les calques dessinés — la
        légende de facto du dossier.
      </Avis>
    );
  }
  return (
    <div className="pile">
      {entrees.map((e) => {
        const d = e.donnees as { texte?: string[] };
        const texte = d.texte ?? [];
        return (
          <div key={e.nom} className="carte">
            <div className="carte__corps pile pile--serree">
              <h3 style={{ margin: 0 }}>{e.nom}</h3>
              <span className="discret">
                {e.occurrences} entrée{e.occurrences > 1 ? "s" : ""} · {e.source}
              </span>
              <ul className="glossaire-legende">
                {texte.map((t, i) => (
                  <li key={i}>{t.replace(/\n+/g, " ")}</li>
                ))}
              </ul>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Nomenclature des calques (la légende de facto) ──────────────────────────

const ROLES: Record<string, string> = {
  DELIMITER: "Délimiteur", FIXTURE: "Équipement", MATERIAL: "Matériau",
  ZONE: "Zone", ANNOTATION: "Annotation", IGNORED: "Autre",
};
const ETATS: Record<string, string> = {
  EXISTING: "Existant", PLANNED: "Projeté", DEMOLISHED: "Démoli",
  UNKNOWN: "—",
};

function SectionNomenclature({ entrees }: { entrees: EntreeGlossaire[] }) {
  if (entrees.length === 0) {
    return (
      <Avis ton="info" permanent>
        Aucune nomenclature de calques n'a pu être lue sur cet instantané.
      </Avis>
    );
  }
  return (
    <div className="carte">
      <div className="carte__corps pile pile--serree">
        <span className="discret">
          Les calques dessinés du dossier, classés — la légende de facto quand les
          plans ne portent pas de bloc de légende.
        </span>
        <div className="defilant">
          <table className="tableau glossaire-table">
            <thead>
              <tr>
                <th>Calque</th>
                <th>Rôle</th>
                <th>État</th>
                <th>Ouvrage</th>
                <th>Matériau</th>
                <th>Planches</th>
              </tr>
            </thead>
            <tbody>
              {entrees.map((e, i) => {
                const d = e.donnees as {
                  role?: string; etat?: string; type_element?: string;
                  materiau?: string | null;
                };
                return (
                  <tr key={`${e.nom}-${i}`}>
                    <td className="mono">{e.nom}</td>
                    <td>{ROLES[d.role ?? ""] ?? d.role}</td>
                    <td>{ETATS[d.etat ?? ""] ?? d.etat}</td>
                    <td>{d.type_element && d.type_element !== "UNKNOWN"
                      ? d.type_element : "—"}</td>
                    <td>{d.materiau || "—"}</td>
                    <td className="mono">{e.occurrences}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Unités ───────────────────────────────────────────────────────────────────

function SectionUnites({ entrees }: { entrees: EntreeGlossaire[] }) {
  if (entrees.length === 0) {
    return <Avis ton="info" permanent>Aucune unité relevée.</Avis>;
  }
  return (
    <div className="carte">
      <div className="carte__corps defilant">
        <table className="tableau glossaire-table">
          <thead>
            <tr>
              <th>Planche</th>
              <th>Unité</th>
              <th>$INSUNITS</th>
              <th>Mesure</th>
              <th>$DIMSCALE</th>
            </tr>
          </thead>
          <tbody>
            {entrees.map((e, i) => {
              const d = e.donnees as {
                insunits?: number; unite?: string; mesure?: string; dimscale?: number;
              };
              // Deux planches de même nom original produisent le même titre :
              // l'index garantit une clé unique (la liste n'est pas réordonnée).
              return (
                <tr key={`${e.nom}-${i}`}>
                  <td className="tronque" title={e.nom} style={{ maxWidth: "22ch" }}>
                    {e.nom}
                  </td>
                  <td>{d.unite}</td>
                  <td className="mono">{d.insunits}</td>
                  <td>{d.mesure}</td>
                  <td className="mono">{d.dimscale}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
